/**
 * Pure decision logic for whether the post-EVBV approval flow should
 * deep-link to the submission tab.
 *
 * @module singleOrderForExternal/utils/shouldShowSubmissionTab
 */
import { AuthStatusOptionModel } from "../../../data-model/authStatusOptions";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { MedicalPaOrderStatus } from "../../../enums/medicalPaOrderStatus";
import { getAuthStatusOfMedicalPaOrderFromAuthOnFile } from "../../../utils/getAuthStatusOfMedicalPaOrder";

/** Minimal shape of an entry in the auth status options selector. */
interface AuthStatusOption {
  id: string;
}

/**
 * Determines whether the submission tab should be shown after EV-BV approval.
 *
 * Mirrors the previous in-component `checkIfSubmissionPageShouldBeShown`
 * behavior:
 * - `AuthOnFile` → never show.
 * - `AuthRequired` with a missing / `PAYER_NOT_SUPPORTED` submission → don't show.
 * - `AuthRequired` with a real submission status → show.
 * - Anything else → don't show.
 *
 * @param authOnFileDetails - Auth-on-file payload for the order
 * @param authStatusOptions - Cached auth-status options from redux
 * @param generalOrder - General order details (used to read submission status)
 * @returns `true` when the submission tab should be offered
 */
export const shouldShowSubmissionTab = (
  authOnFileDetails: AuthOnFile | undefined | null,
  authStatusOptions: AuthStatusOption[] | undefined | null,
  generalOrder: GeneralOrderDetails | undefined | null,
): boolean => {
  const authStatus = getAuthStatusOfMedicalPaOrderFromAuthOnFile(
    authOnFileDetails as AuthOnFile,
    authStatusOptions as AuthStatusOptionModel[],
  ).id;

  if (authStatus === MedicalPaOrdersAuthStatus.AuthOnFile) {
    return false;
  }

  if (authStatus === MedicalPaOrdersAuthStatus.AuthRequired) {
    const submissionStatus = generalOrder?.status?.submission_status;
    if (
      !submissionStatus ||
      submissionStatus === MedicalPaOrderStatus.PAYER_NOT_SUPPORTED
    ) {
      return false;
    }
    return true;
  }

  return false;
};
